/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  createSolid,
  createSolidFromString,
  hasGradient,
} from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { getHTMLInfo } from '../../../components/richText/htmlManipulation';
import { MULTIPLE_VALUE } from '../../../constants';
import { sanitizeRichText } from '../../../utils/sanitizeMarkup';
import { getGradientFallbackColor } from '../../../utils/textGradient';
import { getNormalizedTextListStyle } from './listStyle';

const DEFAULT_MARKER_COLOR = createSolid(0, 0, 0);

function getSemanticListContent(content) {
  const container = document.createElement('template');
  container.innerHTML = content;

  return Array.from(container.content.querySelectorAll('ul, ol'))
    .filter((list) => !list.parentElement?.closest('ul, ol'))
    .map((list) => list.outerHTML)
    .join('');
}

function getSolidMarkerColor(pattern) {
  if (!pattern || pattern === MULTIPLE_VALUE) {
    return null;
  }

  if (!hasGradient(pattern)) {
    return pattern;
  }

  const fallbackColor = getGradientFallbackColor(pattern);
  return fallbackColor ? createSolidFromString(fallbackColor) : null;
}

/**
 * Resolves the marker color in the same order as the text presentation:
 * explicit marker override, element-level text fill, then uniform inline text
 * color. Text color is normally persisted inline, so relying only on
 * element.color makes inherited markers incorrectly fall back to black.
 */
export function getListMarkerColor({ content = '', color, listStyle }) {
  const normalized = getNormalizedTextListStyle(listStyle);
  const explicitMarkerColor = getSolidMarkerColor(normalized.marker.color);
  if (explicitMarkerColor) {
    return explicitMarkerColor;
  }

  const elementColor = getSolidMarkerColor(color);
  if (elementColor) {
    return elementColor;
  }

  const sanitizedContent = sanitizeRichText(content);
  const listContent = getSemanticListContent(sanitizedContent);
  const inlineColor = getSolidMarkerColor(
    getHTMLInfo(listContent || sanitizedContent).color
  );
  return inlineColor || DEFAULT_MARKER_COLOR;
}

export default getListMarkerColor;
